import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { trpc } from "@/trpc/client";
import { Card, DataList, PageHeading } from "@/ui";
import type { Column } from "@/ui";
import {
  AddApiKeyDialog,
  ApiKeySecretDialog,
  ApiKeysTable,
} from "#/components/api-keys/ApiKeys";
import type { CreatedApiKey } from "#/components/api-keys/ApiKeys";
import { docsHref } from "@/lib/routes";
import { AppLink } from "@/ui/AppLink";

const muted = { color: "var(--gs-muted)" };
const mono = { fontFamily: "var(--gs-font-mono)" };
// `.gs-app code` in `src/ui/theme.css` now recolours every bare `<code>` on a
// v2 surface, so this is a pin rather than the fix (issue 704): the same three
// declarations, stated inline where a regression test can read them back.
//
// It keeps the chip and repaints it, where `CopyLine` removes the chip
// outright (`background: transparent`, no border, no padding). Not a drift —
// two different boxes: this one is a bare inline chip in running prose and
// needs a fill to read as one, while `CopyLine`'s `<code>` already sits inside
// its own bordered `--gs-bg` box, where a second chip would draw a box in a box.
//
// The fill is `--gs-bg` (paper) rather than `--gs-surface` (cream) because the
// chip renders inside a `Card`, which fills `--gs-surface` itself — a chip in
// its container's own colour would leave only the 1.38:1 hairline to draw it,
// which is the opposite of "needs a fill to read as one".
const monoChip = { ...mono, color: "var(--gs-text)", background: "var(--gs-bg)", borderColor: "var(--gs-border)" };

// Developer > API keys (#638 created the nav destination and stub; #641
// filled it as the combined "MCP & API" page; #728 split it — MCP server /
// Skill / CLI / use-cases / Credentials moved to their own
// `Developer > MCP & CLI` page (`developer/mcp.tsx`), leaving this one
// API-key management and the in-app API reference (the issue's own AC for
// this destination). The URL is unchanged (D-705-2 precedent: content
// recomposition, not a route move) — see
// `goodsender-autopilot/specs/728-dashboard-nav-reorg/DECISIONS.md` D-728-3
// for why the "API keys" identity kept this URL rather than the "MCP &
// Skills" one (the legacy `/api-keys` and `/sending/api-keys` redirects
// already terminate here).
export const Route = createFileRoute("/dashboard/w/$workspaceId/developer/access")({
  component: DeveloperAccess,
});

/**
 * The real, currently-exposed GoodSender API — read from the Hono route
 * tables (`functions/src/api/email.ts`, `domains.ts`), not the mock's
 * invented `/v1/posts` / `/v1/send` / `/v1/events` trio. There is no public
 * publication API (drafting/publishing happens over MCP or the dashboard
 * itself) and no separate transactional-vs-publication credential split at
 * the API layer — every one of these routes authenticates with the same
 * transactional API key (`functions/src/api.ts`'s `/v1/*` bearer-token
 * middleware). See D-641-5.
 */
const API_ROUTES = [
  { method: "POST", path: "/v1/emails/send", use: "Send a transactional or one-off email" },
  { method: "POST", path: "/v1/emails/template", use: "Send a predefined transactional template" },
  { method: "POST", path: "/v1/emails/consent", use: "Request marketing-send consent from recipients" },
  { method: "GET", path: "/v1/emails", use: "List recipients and their consent/engagement state" },
  { method: "GET", path: "/v1/domains", use: "List and verify sending domains" },
] as const;

// The monospace face lives inside `cell`, not in `cellStyle`: `cellStyle`
// reaches the `<td>` and so applies to the table branch alone, and a method and
// path that render in the body face on a phone are a different design, not the
// same one narrower.
const apiRouteColumns: readonly Column<(typeof API_ROUTES)[number]>[] = [
  {
    key: "method",
    header: "Method",
    cell: (route) => (
      <span className="font-medium" style={mono}>
        {route.method}
      </span>
    ),
  },
  {
    key: "endpoint",
    header: "Endpoint",
    // The endpoint, not the method, heads the card below `md`: five rows here
    // share three methods, so "POST" names none of them.
    primary: true,
    cell: (route) => <span style={mono}>{route.path}</span>,
  },
  { key: "use", header: "Use", cell: (route) => route.use },
];

function ApiReference() {
  const href = docsHref();
  return (
    <Card title="API reference">
      <div className="flex flex-col gap-3">
        <p className="text-sm" style={muted}>
          Every route below authenticates the same way: an{" "}
          <code style={monoChip}>Authorization: Bearer &lt;API key&gt;</code> header, using a key from
          the API keys card below.
        </p>
        <DataList
          columns={apiRouteColumns}
          rows={API_ROUTES}
          // Method AND path: a REST table's identity is the pair, and this one
          // already lists `/v1/emails` under GET while three siblings POST to
          // paths beneath it. No two entries collide today; keying on the pair
          // means adding, say, `DELETE /v1/domains` cannot make them.
          rowKey={(route) => `${route.method} ${route.path}`}
          label="GoodSender API endpoints"
        />
        <p className="text-xs" style={muted}>
          Full request/response shapes are in the{" "}
          {/*
            Accent at rest, like every other in-prose link on a v2 surface
            (`RecentPostsCard`). The sidebar IA reorg retired the two other
            surfaces this used to cite, `SettingsForm` and `DeveloperEmailPage`,
            and the pages their prose links moved onto — Design and Newsletter —
            carry no in-prose link at all, so they are not examples of it.
            #704's `.gs-app a` opt-out takes bare anchors down to
            `color: inherit`, so a link that states nothing is distinguishable
            only by its underline. Inline, not a utility class, to match those
            and because an inline style outranks the `.gs-app a:hover` rule
            that started this.
          */}
          <a
            href={`${href}api-reference/`}
            className="underline"
            style={{ color: "var(--gs-accent)" }}
            target="_blank"
            rel="noopener noreferrer"
          >
            API reference
          </a>
          .
        </p>
      </div>
    </Card>
  );
}

function ApiKeysCard({ workspaceId }: { workspaceId: string }) {
  const [createdApiKey, setCreatedApiKey] = useState<CreatedApiKey | null>(null);
  const { data: usage } = useQuery(
    trpc.authenticatedRouterFunction.getWorkspaceUsage.queryOptions({
      workspaceId,
    }),
  );

  return (
    <Card
      // Titled, but not "API keys" a second time: a card repeating its page's
      // `<h1>` reads as a duplicate heading in an outline rather than as a
      // section of it. `sending/{templates,events,bounces}` drop their card
      // title instead, under the rule "one card on the page, so the `<h1>`
      // already names it" — this page has two (`API reference` above), like
      // `domains.tsx`, and an untitled card following a titled one reads as a
      // continuation of it.
      title="Your keys"
      action={
        <AddApiKeyDialog
          workspaceId={workspaceId}
          onCreate={setCreatedApiKey}
          count={usage?.apiKeyCount}
          limit={usage?.limits.apiKeys}
        />
      }
    >
      <ApiKeysTable workspaceId={workspaceId} onCreate={setCreatedApiKey} />
      <ApiKeySecretDialog apiKey={createdApiKey} onClose={() => setCreatedApiKey(null)} />
    </Card>
  );
}

export function DeveloperAccessPage({ workspaceId }: { workspaceId: string }) {
  return (
    <div className="flex flex-col gap-6">
      <PageHeading
        title="API keys"
        lede={
          <>
            {/*
              "Keys", not "Credentials" (#728): this page owns
              API keys, and the very next sentence sends the reader elsewhere
              for an agent's credentials — introducing it as the credentials
              page contradicted its own cross-link. `settings.tsx`'s footer
              already draws the same line ("its credentials" → MCP & CLI,
              "API keys" → here).
            */}
            Keys and reference for the GoodSender transactional API. Connecting an agent
            and its own credentials live on the{" "}
            {/*
              A page that lost cards says where they went, and says it with a
              link — the same convention `account/UsagePanel.tsx` (with a
              button, since its target is a sibling panel rather than a route)
              and `settings.tsx` follow.
              Link text names the destination on its own, for a screen reader's
              flat link list. Accent at rest, matching every other in-prose
              link on a v2 surface (#704's `.gs-app a` opt-out takes a bare
              anchor to `color: inherit`).
            */}
            <AppLink
              to="/dashboard/w/$workspaceId/developer/mcp"
              params={{ workspaceId }}
              className="underline"
              style={{ color: "var(--gs-accent)" }}
            >
              MCP &amp; CLI page
            </AppLink>
            .
          </>
        }
      />

      <ApiReference />
      <ApiKeysCard workspaceId={workspaceId} />
    </div>
  );
}

function DeveloperAccess() {
  const { workspaceId } = Route.useParams();
  return <DeveloperAccessPage workspaceId={workspaceId} />;
}
